import { useEntitlement } from "../../api/queries";
import { isUnauthorized } from "../../api/errors";
import { ErrorState, LoadingState } from "../../components/AsyncStates";
import { LicenseStatePill } from "../../components/StatePill";
import { UsageMeter } from "../../components/UsageMeter";
import { humanizeFeatureName } from "./entitlementRules";
import "./billing.css";

/** Describes an `EntitlementLimit`'s ceiling in prose, matching the
 * server's own `Limit.Describe()` (server/internal/entitlements/
 * entitlements.go) so the wording never drifts between the two. */
function describeLimit(limit: { Value: number; Unlimited: boolean; Grace: number }): string {
  if (limit.Unlimited) return "unlimited";
  if (limit.Grace > 0) return `${limit.Value} (plus ${limit.Grace} grace)`;
  return `${limit.Value}`;
}

/**
 * The licensing/plan section of the Billing page: tier, state (with a
 * plain-language explanation, never composed client-side — see
 * `EntitlementStatus.message`), live usage against each limit, and which
 * features the plan includes. Rendered only inside `BillingPage`'s existing
 * `canSeeBilling` gate, so `useEntitlement` is always enabled here.
 */
export function LicenseSection() {
  const { data, isPending, isError, error, refetch } = useEntitlement();

  if (isPending) return <LoadingState label="Loading your license…" />;
  if (isError) {
    if (isUnauthorized(error)) return <LoadingState label="Loading your license…" />;
    return (
      <ErrorState
        description={
          error instanceof Error ? error.message : "Couldn't load your license. Check your connection and try again."
        }
        onRetry={refetch}
      />
    );
  }
  if (!data) return null;

  const nodeUsage = data.usage.nodes;
  const envUsage = data.usage.environments;

  return (
    <>
      <div className="license-summary__header">
        <h2 className="license-summary__tier">{data.tier} plan</h2>
        <LicenseStatePill state={data.state} />
      </div>

      <p className="license-summary__message">{data.message}</p>

      {data.state === "stale" && data.lastRefreshError && (
        <p className="license-summary__refresh-note">Last refresh attempt: {data.lastRefreshError}</p>
      )}

      <div className="license-summary__usage">
        <UsageMeter
          label="Machines"
          used={nodeUsage?.used ?? null}
          limit={nodeUsage?.limit ?? (data.nodeLimit.Unlimited ? undefined : data.nodeLimit.Value)}
          grace={nodeUsage?.grace ?? data.nodeLimit.Grace}
          unlimited={nodeUsage?.unlimited ?? data.nodeLimit.Unlimited}
          error={nodeUsage?.error}
        />
        {envUsage ? (
          <UsageMeter
            label="Environments"
            used={envUsage.used}
            limit={envUsage.limit}
            grace={envUsage.grace}
            unlimited={envUsage.unlimited}
            error={envUsage.error}
          />
        ) : (
          <div className="usage-meter">
            <div className="usage-meter__row">
              <span className="usage-meter__label">Environments</span>
              <span className="usage-meter__value">{describeLimit(data.envLimit)} included</span>
            </div>
          </div>
        )}
      </div>

      {data.features.length > 0 && (
        <div className="license-summary__features">
          <h3 className="license-summary__features-heading">Included on this plan</h3>
          <ul className="license-summary__feature-list">
            {data.features.map((name) => (
              <li key={name} className="license-summary__feature-tag">
                {humanizeFeatureName(name)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}
